import { Contract, ContractTransaction } from '@ethersproject/contracts'
import { permit2Address } from '@uniswap/permit2-sdk'
import { CurrencyAmount, Token } from '@uniswap/sdk-core'
import { AVERAGE_L1_BLOCK_TIME } from 'constants/chains'
import { useAccount } from 'hooks/useAccount'
import { useContract, useTokenContract } from 'hooks/useContract'
import { Allowance, AllowanceState } from 'hooks/usePermit2Allowance'
import { usePermitAllowance } from 'hooks/usePermitAllowance'
import { useRevokeTokenAllowance, useTokenAllowance, useUpdateTokenAllowance } from 'hooks/useTokenAllowance'
import { clampToUint160, clampToUint48, robinhoodAllowanceState, selectAllowanceReads } from 'hooks/robinhoodAllowanceState'
import useInterval from 'lib/hooks/useInterval'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHasPendingApproval, useHasPendingRevocation, useTransactionAdder } from 'state/transactions/hooks'
import { ApproveTransactionInfo, TransactionType } from 'state/transactions/types'
import PERMIT2_ABI from 'uniswap/src/abis/permit2.json'
import { UserRejectedRequestError, toReadableError } from 'utils/errors'
import { didUserReject } from 'utils/swapErrorToUserReadableMessage'

enum ApprovalState {
  PENDING,
  SYNCING,
  SYNCED,
}

// How long the on-chain Permit2 -> Universal Router allowance should remain
// valid for once granted. Mirrors usePermitAllowance's signature-mode
// PERMIT_EXPIRATION (30 days) so the two allowance modes behave similarly
// from a user's perspective, even though this one is an on-chain tx rather
// than a signature.
const PERMIT2_ONCHAIN_EXPIRATION_SECONDS = 30 * 24 * 60 * 60

// M4 audit finding 1, Option A: the Robinhood UR pulls ERC-20 input through Permit2's ON-CHAIN
// allowance, which requires calling Permit2.approve(token, spender, uint160 amount, uint48 expiration).
// The shared packages/uniswap/src/abis/permit2.json (release-generated) only includes `allowance`, so
// we build THIS contract from a merged ABI that adds `approve` — self-contained, and immune to the
// shared JSON being regenerated. (The generated Permit2 typechain type lacks `approve` too, hence the
// narrow local write type below.)
const PERMIT2_APPROVE_ABI_ENTRY = {
  type: 'function',
  name: 'approve',
  stateMutability: 'nonpayable',
  inputs: [
    { name: 'token', type: 'address' },
    { name: 'spender', type: 'address' },
    { name: 'amount', type: 'uint160' },
    { name: 'expiration', type: 'uint48' },
  ],
  outputs: [],
} as const
// Module-level (stable reference) so useContract doesn't rebuild the contract every render.
const PERMIT2_ABI_WITH_APPROVE = [...(PERMIT2_ABI as readonly unknown[]), PERMIT2_APPROVE_ABI_ENTRY]

type Permit2WriteContract = Contract & {
  approve(token: string, spender: string, amount: string, expiration: number): Promise<ContractTransaction>
}

/**
 * Robinhood Universal Router allowance mode (M4 audit finding 1, Option A).
 *
 * Unlike `usePermit2Allowance`'s default mode, this hook never requests or
 * consumes a Permit2 EIP-712 signature. M3's Universal Router pulls ERC-20
 * input through Permit2's ON-CHAIN allowance, so the only way to make an
 * ERC-20 input trade buildable is:
 *
 *   1. `ERC20.approve(Permit2, amount)` on-chain, if insufficient.
 *   2. `Permit2.approve(token, spender, amount, expiration)` on-chain, if
 *      insufficient or expired.
 *
 * This returns the same `Allowance` shape `usePermit2Allowance` returns
 * (LOADING / REQUIRED / ALLOWED, with `approve`/`permit`/`revoke` callbacks)
 * so it's a drop-in replacement for Robinhood-router ERC-20 trades in
 * `SwapForm`/`ConfirmSwapModal`/`useConfirmModalState` without touching any
 * of that UI/orchestration code: `needsSetupApproval` drives step 1 exactly
 * as before, and `needsPermitSignature` (kept as the field name to satisfy
 * the shared `Allowance` type) is populated with whether the on-chain
 * Permit2 step is needed, so `permit()` — which those callers already wire
 * up as the second approval step — sends the on-chain Permit2.approve
 * transaction instead of requesting a signature.
 */
export default function useRobinhoodRouterAllowance(
  amount?: CurrencyAmount<Token>,
  spender?: string,
): Allowance {
  const account = useAccount()
  const token = amount?.currency
  const permit2Addr = permit2Address(token?.chainId)

  // Step 1 read: ERC20 -> Permit2
  const { tokenAllowance: erc20ToPermit2Allowance, isSyncing: isErc20ApprovalSyncing } = useTokenAllowance(
    token,
    account.address,
    permit2Addr,
  )
  const updateTokenAllowance = useUpdateTokenAllowance(amount, permit2Addr)
  const revokeTokenAllowance = useRevokeTokenAllowance(token, permit2Addr)
  const isErc20ApprovalPending = useHasPendingApproval(token, permit2Addr)
  const isErc20RevocationPending = useHasPendingRevocation(token, permit2Addr)

  // Step 2 read: Permit2 -> Universal Router, on-chain (never a signature).
  const { permitAllowance: permit2ToURAllowance, expiration: permit2Expiration } = usePermitAllowance(
    token,
    account.address,
    spender,
  )
  const isPermit2ApprovalPending = useHasPendingApproval(token, spender)

  const permit2Contract = useContract(permit2Addr, PERMIT2_ABI_WITH_APPROVE, true)
  const permit2WriteContract = permit2Contract as Permit2WriteContract | null

  // Direct on-chain reads (bypass the redux multicall). The redux multicall Updater
  // (src/lib/state/multicall.tsx) runs through Uniswap's `UniswapInterfaceMulticall` lens, which is
  // NOT deployed on every chain — e.g. Robinhood Chain 4663 only has the standard Multicall3, whose
  // ABI is incompatible — so `useInterfaceMulticall()` is null there and EVERY `useSingleCallResult`
  // read (including `useTokenAllowance` / `usePermitAllowance` above) hangs at `undefined`, leaving
  // this allowance permanently LOADING and blocking the ERC-20 approval prompt. The token/Permit2
  // contracts themselves work for plain `eth_call`s, so read the two allowances directly. Falls back
  // to the multicall reads on chains where the lens exists.
  const erc20Contract = useTokenContract(token?.address, false)
  // Audit #8 (+follow-up): these reads are approval state for one specific
  // {chainId, account, token, spender}. They MUST NOT survive a switch of any of those, or a stale
  // ALLOWED could momentarily classify a new trade as approved. The reads STORE their own identity key
  // and are only consumed when that key matches the CURRENT identity — so a switch invalidates them
  // synchronously in the same render, without depending on a follow-up effect to clear state.
  const identityKey = `${token?.chainId ?? ''}|${(account.address ?? '').toLowerCase()}|${(
    token?.address ?? ''
  ).toLowerCase()}|${(spender ?? '').toLowerCase()}`

  const [directReads, setDirectReads] = useState<{
    identityKey?: string
    erc20?: bigint
    permit2Amount?: bigint
    permit2Expiration?: number
  }>({})

  const refreshDirectReads = useCallback(async () => {
    if (!erc20Contract || !permit2Contract || !account.address || !token || !spender) {
      return
    }
    const captured = identityKey
    try {
      const [erc20Allow, permit2Allow] = await Promise.all([
        erc20Contract.allowance(account.address, permit2Addr),
        permit2Contract.allowance(account.address, token.address, spender),
      ])
      // Stamp the identity these values belong to; consumers ignore them unless it's the current one.
      setDirectReads({
        identityKey: captured,
        erc20: BigInt(erc20Allow.toString()),
        permit2Amount: BigInt(permit2Allow.amount.toString()),
        permit2Expiration: Number(permit2Allow.expiration),
      })
    } catch {
      // Transient RPC failure — keep the previous values and retry on the next interval tick.
    }
  }, [erc20Contract, permit2Contract, account.address, token, spender, permit2Addr, identityKey])
  useEffect(() => {
    refreshDirectReads()
  }, [refreshDirectReads])
  useInterval(refreshDirectReads, AVERAGE_L1_BLOCK_TIME)

  // Same transient-loading treatment usePermit2Allowance gives its (single)
  // approval step, scoped here to step 1 (ERC20 -> Permit2): avoids
  // re-prompting for an already-submitted approval before it's re-observed.
  const [approvalState, setApprovalState] = useState(ApprovalState.SYNCED)
  const isApprovalLoading = approvalState !== ApprovalState.SYNCED
  useEffect(() => {
    if (isErc20ApprovalPending) {
      setApprovalState(ApprovalState.PENDING)
    } else {
      setApprovalState((state) => {
        if (state === ApprovalState.PENDING && isErc20ApprovalSyncing) {
          return ApprovalState.SYNCING
        } else if (state === ApprovalState.SYNCING && !isErc20ApprovalSyncing) {
          return ApprovalState.SYNCED
        }
        return state
      })
    }
  }, [isErc20ApprovalPending, isErc20ApprovalSyncing])

  // Both on-chain expirations/allowances should be rechecked at an interval,
  // in unix seconds (Permit2's `expiration` is a uint48 unix timestamp).
  const [now, setNow] = useState(() => Math.floor((Date.now() + AVERAGE_L1_BLOCK_TIME) / 1000))
  useInterval(
    useCallback(() => setNow(Math.floor((Date.now() + AVERAGE_L1_BLOCK_TIME) / 1000)), []),
    AVERAGE_L1_BLOCK_TIME,
  )

  const requiredAmount = useMemo<bigint | undefined>(
    () => (amount ? BigInt(amount.quotient.toString()) : undefined),
    [amount],
  )
  // Prefer the direct eth_call reads (work on every chain, and only when they belong to the CURRENT
  // identity); otherwise fall back to the multicall reads on chains where Uniswap's InterfaceMulticall
  // lens is deployed. Identity-gating lives in the pure `selectAllowanceReads` (unit-tested).
  const {
    erc20ToPermit2: erc20ToPermit2Raw,
    permit2ToUR: permit2ToURRaw,
    permit2Expiration: effectivePermit2Expiration,
  } = selectAllowanceReads({
    identityKey,
    directReads,
    multicallErc20: erc20ToPermit2Allowance ? BigInt(erc20ToPermit2Allowance.quotient.toString()) : undefined,
    multicallPermit2: permit2ToURAllowance ? BigInt(permit2ToURAllowance.quotient.toString()) : undefined,
    multicallPermit2Expiration: permit2Expiration,
  })

  const decision = useMemo(
    () =>
      robinhoodAllowanceState({
        erc20ToPermit2: erc20ToPermit2Raw,
        permit2ToUR: permit2ToURRaw,
        permit2Expiration: effectivePermit2Expiration,
        amount: requiredAmount ?? 0n,
        now,
      }),
    [erc20ToPermit2Raw, permit2ToURRaw, effectivePermit2Expiration, requiredAmount, now],
  )

  const addTransaction = useTransactionAdder()

  // Intentionally fire-and-forget past the tx submission: `addTransaction` registers the pending
  // tx in redux, and `useHasPendingApproval`/`isErc20ApprovalPending` (above) is what the caller
  // polls to know when it lands — this callback does not await confirmation itself.
  const approve = useCallback(async () => {
    const { response, info } = await updateTokenAllowance()
    addTransaction(response, info)
  }, [addTransaction, updateTokenAllowance])

  const revoke = useCallback(async () => {
    const { response, info } = await revokeTokenAllowance()
    addTransaction(response, info)
  }, [addTransaction, revokeTokenAllowance])

  // The on-chain Permit2.approve(token, spender, amount, expiration) write.
  // This is the primitive the audit calls out as missing: it is a
  // transaction, never a signature, and it is what actually makes
  // Permit2.allowance(owner, token, UniversalRouter) nonzero on-chain.
  // Same fire-and-forget pending-tx pattern as `approve` above: `addTransaction` +
  // `useHasPendingApproval` (isPermit2ApprovalPending) track confirmation, not an await here.
  const permit = useCallback(async () => {
    if (!token) {
      throw new Error('missing token')
    }
    if (!spender) {
      throw new Error('missing spender')
    }
    if (!permit2WriteContract) {
      throw new Error('missing Permit2 contract')
    }
    if (requiredAmount === undefined) {
      throw new Error('missing amount')
    }

    const clampedAmount = clampToUint160(requiredAmount)
    const expiration = clampToUint48(Math.floor(Date.now() / 1000) + PERMIT2_ONCHAIN_EXPIRATION_SECONDS)

    let response: ContractTransaction
    try {
      response = await permit2WriteContract.approve(token.address, spender, clampedAmount.toString(), expiration)
    } catch (error) {
      if (didUserReject(error)) {
        throw new UserRejectedRequestError(`${token.symbol ?? 'Token'} Permit2 allowance failed: User rejected`)
      }
      throw toReadableError(`${token.symbol ?? 'Token'} Permit2 allowance failed:`, error)
    }

    const info: ApproveTransactionInfo = {
      type: TransactionType.APPROVAL,
      tokenAddress: token.address,
      spender,
      amount: clampedAmount.toString(),
    }
    addTransaction(response, info)
  }, [addTransaction, permit2WriteContract, requiredAmount, spender, token])

  // Not wired into any current UI flow (ConfirmSwapModal drives the two
  // steps independently via `approve`/`permit`), but part of the shared
  // `Allowance` type's `AllowanceRequired` shape, so it's implemented for
  // type-compatibility and any future caller that wants a single call.
  const approveAndPermit = useCallback(async () => {
    if (decision.kind === 'REQUIRED' && decision.needsSetupApproval) {
      await approve()
    }
    if (decision.kind === 'REQUIRED' && decision.needsPermit2Approval) {
      await permit()
    }
  }, [approve, decision, permit])

  return useMemo(() => {
    if (!token) {
      // Native input never needs an approval.
      return { state: AllowanceState.ALLOWED }
    }

    if (decision.kind === 'LOADING') {
      return { state: AllowanceState.LOADING }
    }

    if (decision.kind === 'REQUIRED') {
      return {
        token,
        state: AllowanceState.REQUIRED,
        isApprovalLoading: decision.needsSetupApproval ? isApprovalLoading : false,
        isApprovalPending: isErc20ApprovalPending || isPermit2ApprovalPending,
        isRevocationPending: isErc20RevocationPending,
        approveAndPermit,
        approve,
        permit,
        revoke,
        needsSetupApproval: decision.needsSetupApproval,
        needsPermitSignature: decision.needsPermit2Approval,
        allowedAmount: erc20ToPermit2Allowance ?? CurrencyAmount.fromRawAmount(token, 0),
      }
    }

    // decision.kind === 'ALLOWED'
    return {
      token,
      state: AllowanceState.ALLOWED,
      // Never a signature in this mode — see module doc.
      permitSignature: undefined,
      needsSetupApproval: false,
      needsPermitSignature: false,
    }
  }, [
    token,
    decision,
    isApprovalLoading,
    isErc20ApprovalPending,
    isPermit2ApprovalPending,
    isErc20RevocationPending,
    approveAndPermit,
    approve,
    permit,
    revoke,
    erc20ToPermit2Allowance,
  ])
}
